import React, { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles, tokens, Skeleton, SkeletonItem, mergeClasses } from '@fluentui/react-components';
import CopilotIcon from '../../../assets/office/ribbon/Copilot.svg';
import { useWordSlateEditor } from './WordSlateEditorContext';
import { llmService } from '../../../llm/services/LLMEnhancementService';
import { azureOpenAIService } from '../../../llm/services/AzureOpenAIService';
import summaryPromptText from '../../../prompts/summary-prompt.txt?raw';
import { DEFAULT_SUMMARY_TEXT, DEFAULT_SUMMARY_MARKDOWN } from './defaultSummary';

type TopOfDocState = 'loading' | 'generating' | 'rest';

interface TopOfDocStyleCProps {
  summaryText?: string;
  onCopilotClick?: () => void;
  onSummaryClick?: (summary: string) => void;
  onMessageSend?: (message: string) => void;
  onPillClick?: (pillText: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '816px', // Fixed to Slate editor width
    maxWidth: '816px',
    minWidth: '816px',
    padding: '0',
    boxSizing: 'border-box',
    margin: '0 auto', // Center in parent container
  },
  
  // Summary card - takes most of the width, inline text that wraps to two lines
  summaryCard: {
    position: 'relative' as const,
    flex: '1 1 0',
    minWidth: '0',
    height: '48px', // Fixed height: 5px padding top + 18px line 1 + 18px line 2 + 7px padding bottom
    padding: '6.5px 12px 5.5px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxSizing: 'border-box',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Summary card faded state when Copilot is hovered
  summaryCardFaded: {
    opacity: 0.5,
  },
  
  // Wrapper for text content with line clamp
  summaryTextWrapper: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '15px',
  },
  
  // Bottom gradient overlay - fades from transparent to white (top to bottom)
  bottomGradient: {
    position: 'absolute' as const,
    left: '0',
    right: '0',
    bottom: '0',
    height: '28px',
    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)',
    pointerEvents: 'none' as const,
    borderRadius: '0 0 12px 12px', // Match card's bottom border radius
  },
  
  // Summary card for generating state - flex column layout
  summaryCardGenerating: {
    flex: '1 1 0',
    minWidth: '0',
    height: '60px', // Fixed height: same as summaryCard
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 14px 10px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxSizing: 'border-box',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Icon styling - fixed width, aligned to top
  icon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
    marginTop: '0px', // Align with first line of text
  },
  
  // Text container - holds title and summary, allows wrapping
  textContainer: {
    flex: '1 1 0',
    minWidth: '0',
    display: 'inline',
  },
  
  // Text styles - title inline with summary
  titleText: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '15px',
    color: tokens.colorNeutralForeground2,
    fontFamily: '"Segoe UI", sans-serif',
    marginRight: '6px',
    flexShrink: 0,
  },
  
  // Summary text - flows inline and wraps, aligned with title on second line
  summaryText: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '15px',
    color: tokens.colorNeutralForeground2,
    fontFamily: '"Segoe UI", sans-serif',
  },
  
  // First row wrapper - contains title and first part of summary inline
  summaryFirstRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0',
    width: '100%',
    minWidth: '0',
    overflow: 'hidden',
  },
  
  // Second row wrapper - for overflow summary text
  summarySecondRow: {
    display: 'block',
    width: '100%',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  
  // Wrapper for inline text flow (title + summary) - used in rest state
  summaryInlineWrapper: {
    display: 'inline',
    lineHeight: '20px',
  },
  
  generatingText: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '15px',
    color: tokens.colorNeutralForeground4,
    fontFamily: '"Segoe UI", sans-serif',
  },
  
  // Copilot button - same style as DAB floating button
  copilotButton: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '16px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  // Inner button wrapper
  copilotInnerButton: {
    backgroundColor: 'transparent',
    borderRadius: '4px',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
  },
  
  copilotIconImg: {
    width: '24px',
    height: '24px',
    flexShrink: 0,
  },
  
  // Skeleton styles
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
    marginTop: '2px',
  },
  
  skeletonRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  
  // First skeleton in row 1 - 64px wide
  skeletonTextShort: {
    width: '64px',
    height: '12px',
    borderRadius: '4px',
  },
  
  // Second skeleton in row 1 - 120px wide
  skeletonTextMedium: {
    width: '120px',
    height: '12px',
    borderRadius: '4px',
  },
  
  // Skeleton in row 2 - 200px wide
  skeletonTextLong: {
    width: '200px',
    height: '12px',
    borderRadius: '4px',
  },
  
  // Skeleton for DAB-style button
  skeletonButton: {
    width: '44px',
    height: '44px',
    borderRadius: '16px',
  },
  
  // Wrapper for Copilot button and pills
  copilotWrapper: {
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0px',
    overflow: 'visible',
  },
  
  // Pills container (shown on hover, positioned to the left, horizontal layout)
  pillsContainer: {
    display: 'none',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    position: 'absolute' as const,
    right: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginRight: '8px',
    zIndex: 10,
    background: 'linear-gradient(to right, rgba(245, 245, 245, 0) 0%, rgba(245, 245, 245, 1) 120px, rgba(245, 245, 245, 1) 100%)',
    paddingLeft: '120px',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  
  pillsContainerVisible: {
    display: 'flex',
  },
  
  // Individual pill - matching DAB pill style
  pill: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    padding: '0 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    cursor: 'pointer',
    opacity: 0,
    transform: 'scale(0.9)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.9)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.15s',
    animationFillMode: 'forwards',
    animationTimingFunction: 'ease-out',
    transition: 'all 0.1s ease',
    whiteSpace: 'nowrap' as const,
    boxSizing: 'border-box' as const,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow4,
      border: '1px solid transparent',
    },
  },
  
  pillText: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground2,
    fontFamily: '"Segoe UI", sans-serif',
  },
});

export const TopOfDocStyleC: React.FC<TopOfDocStyleCProps> = ({ 
  summaryText: propSummaryText,
  onCopilotClick,
  onSummaryClick,
  onPillClick,
}) => {
  const styles = useStyles();
  const [state, setState] = useState<TopOfDocState>('loading');
  const [summaryText, setSummaryText] = useState<string>(propSummaryText || '');
  const [fullSummaryText, setFullSummaryText] = useState<string>(''); // Full markdown version for Copilot pane
  const [isCopilotHovered, setIsCopilotHovered] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { commands } = useWordSlateEditor();

  // Helper function to extract plain text from markdown (full text, no truncation)
  const extractPlainTextSummary = (markdown: string): string => {
    // Remove markdown formatting
    const plainText = markdown
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
      .replace(/\*([^*]+)\*/g, '$1') // Remove italic
      .replace(/^#+\s*/gm, '') // Remove headers
      .replace(/^-\s+/gm, '• ') // Replace list dashes with bullet
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .replace(/\s+/g, ' ') // Normalize multiple spaces
      .trim();
    
    return plainText;
  };

  // Handle Copilot button hover
  const handleCopilotMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsCopilotHovered(true);
  };

  const handleCopilotMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsCopilotHovered(false);
    }, 100);
  };

  // Pills for Copilot button
  const copilotPills = [
    'Prepare me for a meeting',
    'Get writing suggestions',
    'More',
  ];

  // Handle pill click - send message to Copilot
  const handlePillClick = (pillText: string) => {
    if (pillText === 'More') {
      // For More, just open Copilot panel
      onCopilotClick?.();
    } else {
      // Send pill text to Copilot
      onPillClick?.(pillText);
    }
  };

  // Generate summary using LLM
  const generateSummary = useCallback(async () => {
    try {
      // Get document text from Slate editor
      const documentText = commands.getDocumentText();
      
      if (!documentText || documentText.length < 50) {
        setSummaryText('Document is too short to summarize.');
        setFullSummaryText('Document is too short to summarize.');
        setState('rest');
        return;
      }

      // Priority 1: Try Azure OpenAI if configured
      if (azureOpenAIService.isConfigured()) {
        try {
          console.log('Using Azure OpenAI for summary generation...');
          const response = await azureOpenAIService.generateSummary(documentText);
          const fullText = response.trim();
          setFullSummaryText(fullText); // Store full markdown version
          setSummaryText(extractPlainTextSummary(fullText)); // Store plain text for card
          setState('rest');
          return;
        } catch (error) {
          console.error('Azure OpenAI failed, trying fallback:', error);
        }
      }

      // Priority 2: Try Microsoft LLM if enabled and authenticated
      const isAuthenticated = await llmService.isAuthenticated();
      const config = llmService.getConfig();
      
      if (config.isEnabled && isAuthenticated) {
        try {
          // Use prompt from external txt file
          const prompt = `${summaryPromptText}
${documentText}`;
          const response = await llmService.generateResponse(prompt);
          const fullText = response.trim();
          setFullSummaryText(fullText); // Store full markdown version
          setSummaryText(extractPlainTextSummary(fullText)); // Store plain text for card
          setState('rest');
          return;
        } catch (error) {
          console.error('Microsoft LLM failed:', error);
        }
      }

      // Fallback: use default summary when AI is not available
      console.log('Using default summary (AI not available)');
      setSummaryText(DEFAULT_SUMMARY_TEXT);
      setFullSummaryText(DEFAULT_SUMMARY_MARKDOWN);
      setState('rest');
    } catch (error) {
      console.error('Failed to generate summary:', error);
      // Fallback: use default summary
      console.log('Using default summary (error occurred)');
      setSummaryText(DEFAULT_SUMMARY_TEXT);
      setFullSummaryText(DEFAULT_SUMMARY_MARKDOWN);
      setState('rest');
    }
  }, [commands]);

  // Loading states and summary generation
  useEffect(() => {
    // After 1 second, transition from loading to generating
    const loadingTimer = setTimeout(() => {
      setState('generating');
      // Start generating summary
      generateSummary();
    }, 1000);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, [generateSummary]);

  // Loading state - summary skeleton, but Copilot button is ready
  if (state === 'loading') {
    return (
      <div className={styles.container}>
        {/* Summary skeleton - two rows */}
        <div className={mergeClasses(styles.summaryCard, isCopilotHovered && styles.summaryCardFaded)}>
          <Skeleton>
            <div className={styles.skeletonContainer}>
              <div className={styles.skeletonRow}>
                <SkeletonItem className={styles.skeletonTextShort} />
              </div>
              <div className={styles.skeletonRow}>
                <SkeletonItem className={styles.skeletonTextLong} />
              </div>
            </div>
          </Skeleton>
        </div>
        
        {/* Copilot button - ready immediately with pills on hover */}
        <div 
          className={styles.copilotWrapper}
          onMouseEnter={handleCopilotMouseEnter}
          onMouseLeave={handleCopilotMouseLeave}
        >
          <div 
            className={styles.copilotButton}
            onClick={onCopilotClick}
          >
            <div className={styles.copilotInnerButton}>
              <img 
                src={CopilotIcon} 
                alt="Copilot" 
                className={styles.copilotIconImg}
              />
            </div>
          </div>
          
          {/* Pills - shown on hover */}
          <div className={mergeClasses(styles.pillsContainer, isCopilotHovered && styles.pillsContainerVisible)}>
            {copilotPills.map((pill, index) => (
              <div 
                key={index}
                className={styles.pill}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => handlePillClick(pill)}
              >
                <span className={styles.pillText}>{pill}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Generating state - summary generating, copilot button loaded
  if (state === 'generating') {
    return (
      <div className={styles.container}>
        {/* Summary - generating, title on first line, generating text on second line */}
        <div className={mergeClasses(styles.summaryCard, isCopilotHovered && styles.summaryCardFaded)}>
          <div className={styles.summaryTextWrapper}>
            <span className={styles.titleText}>Summary</span>
            <br />
            <span className={styles.generatingText}>Generating summary...</span>
          </div>
        </div>
        
        {/* Copilot button with pills on hover */}
        <div 
          className={styles.copilotWrapper}
          onMouseEnter={handleCopilotMouseEnter}
          onMouseLeave={handleCopilotMouseLeave}
        >
          <div 
            className={styles.copilotButton}
            onClick={onCopilotClick}
          >
            <div className={styles.copilotInnerButton}>
              <img 
                src={CopilotIcon} 
                alt="Copilot" 
                className={styles.copilotIconImg}
              />
            </div>
          </div>
          
          {/* Pills - shown on hover */}
          <div className={mergeClasses(styles.pillsContainer, isCopilotHovered && styles.pillsContainerVisible)}>
            {copilotPills.map((pill, index) => (
              <div 
                key={index}
                className={styles.pill}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => handlePillClick(pill)}
              >
                <span className={styles.pillText}>{pill}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Rest state - all loaded
  return (
    <div className={styles.container}>
      {/* Summary - title + summary text inline, wrapping to max 2 lines */}
      <div 
        className={mergeClasses(styles.summaryCard, isCopilotHovered && styles.summaryCardFaded)}
        onClick={() => onSummaryClick?.(fullSummaryText || summaryText)}
      >
        <div className={styles.summaryTextWrapper}>
          <span className={styles.titleText}>Summary</span>
          <span className={styles.summaryText}>{summaryText}</span>
        </div>
        {/* Bottom gradient overlay - fades second line */}
        <div className={styles.bottomGradient} />
      </div>
      
      {/* Copilot button with pills on hover */}
      <div 
        className={styles.copilotWrapper}
        onMouseEnter={handleCopilotMouseEnter}
        onMouseLeave={handleCopilotMouseLeave}
      >
        <div 
          className={styles.copilotButton}
          onClick={onCopilotClick}
        >
          <div className={styles.copilotInnerButton}>
            <img 
              src={CopilotIcon} 
              alt="Copilot" 
              className={styles.copilotIconImg}
            />
          </div>
        </div>
        
        {/* Pills - shown on hover */}
        <div className={mergeClasses(styles.pillsContainer, isCopilotHovered && styles.pillsContainerVisible)}>
          {copilotPills.map((pill, index) => (
            <div 
              key={index}
              className={styles.pill}
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => handlePillClick(pill)}
            >
              <span className={styles.pillText}>{pill}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopOfDocStyleC;
